import React from "react";
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from "reactstrap";
import Select from "react-select";
import { PrivacySettingsValues, SettingsProps } from "../../types/settings";
import SettingsButtons from "./SettingsButtons";
import { privacySettingsValidation } from "rules/settings";
import { Formik } from "formik";

const retentionOptions = [
  { value: '30days', label: '30 Days' },
  { value: '60days', label: '60 Days' },
  { value: '90days', label: '90 Days' },
  { value: '180days', label: '180 Days' },
  { value: '1year', label: '1 Year' }
];

const orderRetentionOptions = [
  { value: '1year', label: '1 Year' },
  { value: '2years', label: '2 Years' },
  { value: '3years', label: '3 Years' },
  { value: '5years', label: '5 Years' },
  { value: 'forever', label: 'Forever' }
];

const PrivacySettings: React.FC<SettingsProps> = ({ 
  onGlobalCancel, 
  isSaving, 
  onSave 
}) => {
  const initialValues: PrivacySettingsValues = {
    collectAnalytics: false,
    cookieConsent: false,
    customerDataRetention: retentionOptions[0],
    orderDataRetention: orderRetentionOptions[0],
    allowDataExport: false,
    allowDataDeletion: false,
    privacyContact: "",
  };

  const handleSave = async (values: PrivacySettingsValues) => {
    await onSave(async () => {
      console.log("Form submitted with values:", values);
      await new Promise(resolve => setTimeout(resolve, 1000));
      // TODO: Add actual API call here
    });
  };

  return (
    <div className="bg-transparent">
      <h5 className="mb-4">Customer Privacy Settings</h5>
      <Formik
        initialValues={initialValues}
        validationSchema={privacySettingsValidation}
        onSubmit={handleSave}
        validateOnMount={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Card className="mb-4">
              <CardBody>
                <h6 className="mb-3">Data Collection</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="collectAnalytics"
                        name="collectAnalytics"
                        onChange={handleChange}
                        checked={values.collectAnalytics}
                      />
                      <Label check for="collectAnalytics">
                        Collect Analytics Data
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="cookieConsent"
                        name="cookieConsent"
                        onChange={handleChange}
                        checked={values.cookieConsent}
                      />
                      <Label check for="cookieConsent">
                        Require Cookie Consent
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Data Retention</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="customerDataRetention">Customer Data Retention Period</Label>
                      <Select
                        id="customerDataRetention"
                        options={retentionOptions}
                        value={values.customerDataRetention}
                        onChange={(selected) => setFieldValue("customerDataRetention", selected)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="orderDataRetention">Order History Retention</Label>
                      <Select
                        id="orderDataRetention"
                        options={orderRetentionOptions}
                        value={values.orderDataRetention}
                        onChange={(selected) => setFieldValue("orderDataRetention", selected)}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Privacy Controls</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="allowDataExport"
                        name="allowDataExport"
                        onChange={handleChange}
                        checked={values.allowDataExport}
                      />
                      <Label check for="allowDataExport">
                        Allow Data Export
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="allowDataDeletion"
                        name="allowDataDeletion"
                        onChange={handleChange}
                        checked={values.allowDataDeletion}
                      />
                      <Label check for="allowDataDeletion">
                        Allow Account Deletion
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="privacyContact">Privacy Contact Email</Label>
                      <Input
                        type="email"
                        id="privacyContact"
                        name="privacyContact"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.privacyContact}
                        placeholder="Enter privacy contact email"
                      />
                      {touched.privacyContact && errors.privacyContact && (
                        <div className="text-danger mt-1">{errors.privacyContact}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <SettingsButtons
              onSave={handleSubmit}
              onCancel={onGlobalCancel}
              saveDisabled={Object.keys(errors).length > 0}
              isSaving={isSaving}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PrivacySettings; 